/**
 * ivyIntentExecutor.ts — Phase 3
 *
 * Dispatches parsed Ivy intents to their handlers. Called from IvyDock after
 * every response that carries a non-conversation intent.
 *
 * Design constraints:
 *   - This is a plain TS module (not a React component). It cannot use React
 *     hooks. Navigator + state setters are passed in via ExecutorContext.
 *   - Tier 1: execute immediately, write audit log, show AlertBanner.
 *   - Tier 2: set pendingConfirmation; execution happens only if user confirms.
 *   - Blocked: show an AlertBanner explanation; no execution.
 *
 * All API calls made here rely on existing api.ts functions or apiFetch.
 * Actions whose backend endpoints are not yet implemented (e.g. phase-gated
 * memory actions) navigate to the relevant page and note the intent in the
 * audit log — they do not fail silently.
 */

import type { NavigateFunction } from 'react-router'
import type { ParsedIntent } from './ivyIntentTypes'
import {
  analyzeVoice,
  bulkAnalyzeTranscribed,
  addCompetitorShow,
  removeCompetitorShow,
  createDeal,
  createGuest,
  createPlannedEpisode,
  deletePlannedEpisode,
  detectClips,
  dismissAlert,
  endLaunch,
  extractClipsFromTranscript,
  generateRoadmap,
  pulseLaunch,
  routeClipToSocial,
  scoreGuest,
  startLaunch,
  updateClip,
  updateDeal,
  updatePlannedEpisode,
  updatePreferences,
  updateProfile,
  updateRoadmapEntry,
  syncPlatform,
  logIvyAction,
  apiFetch,
} from './api'

// ── ExecutorContext ───────────────────────────────────────────────────────────

export interface StatusBanner {
  type: 'info' | 'warning' | 'error' | 'success'
  message: string
}

export interface PendingConfirmation {
  message: string
  actionLabel?: string
  onConfirm: () => void
}

export interface ExecutorContext {
  navigate: NavigateFunction
  sessionId: string
  podcastId: string
  setStatusBanner: (banner: StatusBanner | null) => void
  setPendingConfirmation: (conf: PendingConfirmation | null) => void
}

// ── executeIntent — main entry point ─────────────────────────────────────────

export async function executeIntent(
  intent: ParsedIntent,
  ctx: ExecutorContext
): Promise<void> {
  if (intent.type === 'conversation') return

  if (intent.type === 'blocked') {
    ctx.setStatusBanner({ type: 'warning', message: intent.reason })
    return
  }

  if (intent.tier === 1) {
    await executeTier1(intent.action, intent.params, ctx)
    return
  }

  if (intent.tier === 2) {
    ctx.setPendingConfirmation({
      message: intent.confirmationMessage ?? `Proceed with "${intent.action}"?`,
      onConfirm: () => {
        ctx.setPendingConfirmation(null)
        void executeTier2(intent.action, intent.params, ctx)
      },
    })
  }
}

// ── Audit log helper ──────────────────────────────────────────────────────────

async function audit(
  action: string,
  tier: 1 | 2,
  params: Record<string, unknown>,
  sessionId: string,
  status: 'completed' | 'failed',
  errorMessage?: string
): Promise<void> {
  try {
    await logIvyAction({ action, tier, params, sessionId, status, errorMessage })
  } catch {
    // Audit is best-effort — never fail the UX on log write failure
  }
}

// ── Tier 1 dispatcher ─────────────────────────────────────────────────────────

async function executeTier1(
  action: string,
  params: Record<string, unknown>,
  ctx: ExecutorContext
): Promise<void> {
  const { navigate, sessionId, podcastId, setStatusBanner } = ctx

  try {
    switch (action) {
      // ── Navigation ──────────────────────────────────────────────────────────

      case 'navigate': {
        const routeKey = params.routeKey as string
        const routePathMap: Record<string, string> = {
          dashboard: '/dashboard', signalcast: '/signalcast', trends: '/trends',
          youtube: '/youtube', guests: '/guests', retention: '/retention',
          clips: '/clips', castback: '/castback', voice: '/voice',
          monetization: '/monetization', competitive: '/competitive',
          launch: '/launch', brain: '/brain', audience: '/audience',
          integration: '/integration', settings: '/settings', ivy: '/ivy',
        }
        const path = routePathMap[routeKey]
        if (!path) {
          setStatusBanner({ type: 'error', message: `Unknown route: ${routeKey}` })
          return
        }
        navigate(path)
        setStatusBanner({ type: 'success', message: `Navigated to ${routeKey}` })
        break
      }

      case 'open_episode': {
        navigate('/brain')
        setStatusBanner({ type: 'info', message: 'Opened Episode Brain' })
        break
      }

      case 'open_planned_episode': {
        const id = params.plannedEpisodeId as string
        if (!id) break
        navigate(`/brain/plan/${id}`)
        setStatusBanner({ type: 'info', message: 'Opened episode workspace' })
        break
      }

      case 'open_guest': {
        navigate('/guests')
        setStatusBanner({ type: 'info', message: 'Opened Guest Intelligence' })
        break
      }

      case 'open_sponsor': {
        navigate('/monetization')
        setStatusBanner({ type: 'info', message: 'Opened Monetization' })
        break
      }

      case 'open_clip_candidate': {
        navigate('/clips')
        setStatusBanner({ type: 'info', message: 'Opened Clip Intelligence' })
        break
      }

      case 'open_settings_tab': {
        navigate('/settings')
        setStatusBanner({ type: 'info', message: 'Opened Settings' })
        break
      }

      // ── Inkwell / generation ────────────────────────────────────────────────

      case 'generate_title': {
        // Navigate to brain; generation requires topic which must be selected in UI
        navigate('/brain')
        setStatusBanner({ type: 'info', message: 'Navigated to Episode Brain — select an episode to generate titles' })
        break
      }

      case 'generate_brief': {
        navigate('/brain')
        setStatusBanner({ type: 'info', message: 'Navigated to Episode Brain — select an episode to generate a brief' })
        break
      }

      case 'generate_clip_proposals': {
        const plannedEpisodeId = params.plannedEpisodeId as string
        if (!plannedEpisodeId) {
          setStatusBanner({ type: 'error', message: 'No episode ID provided for clip proposals' })
          return
        }
        await extractClipsFromTranscript(plannedEpisodeId)
        setStatusBanner({ type: 'success', message: 'Clip proposals generated' })
        break
      }

      // ── Analysis triggers ───────────────────────────────────────────────────

      case 'run_signalcast_prediction': {
        // Full prediction input requires episode data — navigate to SignalCast
        navigate('/signalcast')
        setStatusBanner({ type: 'info', message: 'Navigated to SignalCast — run your prediction from here' })
        break
      }

      case 'run_voice_analysis': {
        const episodeId = params.episodeId as string
        if (!episodeId) {
          setStatusBanner({ type: 'error', message: 'No episode ID provided for voice analysis' })
          return
        }
        await analyzeVoice(episodeId)
        setStatusBanner({ type: 'success', message: 'Voice analysis started' })
        break
      }

      case 'detect_clips': {
        const episodeId = params.episodeId as string
        if (!episodeId) {
          setStatusBanner({ type: 'error', message: 'No episode ID provided for clip detection' })
          return
        }
        await detectClips(episodeId)
        setStatusBanner({ type: 'success', message: 'Clip detection triggered' })
        break
      }

      case 'start_episode_roadmap': {
        await generateRoadmap(podcastId)
        setStatusBanner({ type: 'success', message: 'Episode roadmap generation started' })
        break
      }

      case 'pulse_launch': {
        const launchEventId = params.launchEventId as string
        if (!launchEventId) {
          setStatusBanner({ type: 'error', message: 'No launch event ID provided' })
          return
        }
        await pulseLaunch(launchEventId)
        setStatusBanner({ type: 'success', message: 'Launch data refreshed' })
        break
      }

      // ── Guest / content updates ─────────────────────────────────────────────

      case 'mark_guest_watchlist': {
        const guestId = params.guestId as string
        if (!guestId) {
          setStatusBanner({ type: 'error', message: 'No guest ID provided' })
          return
        }
        // `on_watchlist` toggle — server handles the field; updateGuest accepts partial
        await apiFetch(`/guests/${guestId}`, {
          method: 'PATCH',
          body: JSON.stringify({ on_watchlist: true }),
        })
        setStatusBanner({ type: 'success', message: 'Guest added to watchlist' })
        break
      }

      case 'dismiss_alert': {
        const alertId = params.alertId as string
        if (!alertId) {
          setStatusBanner({ type: 'error', message: 'No alert ID provided' })
          return
        }
        await dismissAlert(alertId)
        setStatusBanner({ type: 'success', message: 'Alert dismissed' })
        break
      }

      case 'save_planned_episode_title': {
        const plannedEpisodeId = params.plannedEpisodeId as string
        const title = params.title as string
        if (!plannedEpisodeId || !title) {
          setStatusBanner({ type: 'error', message: 'Missing episode ID or title' })
          return
        }
        await updatePlannedEpisode(plannedEpisodeId, { selected_title: title })
        setStatusBanner({ type: 'success', message: 'Episode title saved' })
        break
      }

      case 'mark_planned_episode_briefed': {
        const plannedEpisodeId = params.plannedEpisodeId as string
        if (!plannedEpisodeId) {
          setStatusBanner({ type: 'error', message: 'No episode ID provided' })
          return
        }
        await updatePlannedEpisode(plannedEpisodeId, { status: 'briefed' })
        setStatusBanner({ type: 'success', message: 'Episode marked as briefed' })
        break
      }

      case 'update_clip_status': {
        const clipId = params.clipId as string
        const status = params.status as string
        if (!clipId || !status) {
          setStatusBanner({ type: 'error', message: 'Missing clip ID or status' })
          return
        }
        await updateClip(clipId, { status } as Parameters<typeof updateClip>[1])
        setStatusBanner({ type: 'success', message: `Clip status updated to ${status}` })
        break
      }

      // ── Profile / preferences ───────────────────────────────────────────────

      case 'change_preferred_name': {
        const name = params.name as string
        if (!name) {
          setStatusBanner({ type: 'error', message: 'No name provided' })
          return
        }
        await updateProfile({ preferred_name: name } as Parameters<typeof updateProfile>[0])
        setStatusBanner({ type: 'success', message: `Preferred name updated to "${name}"` })
        break
      }

      case 'update_profile_display': {
        const displayName = params.displayName as string
        if (!displayName) {
          setStatusBanner({ type: 'error', message: 'No display name provided' })
          return
        }
        await updateProfile({ full_name: displayName } as Parameters<typeof updateProfile>[0])
        setStatusBanner({ type: 'success', message: 'Display name updated' })
        break
      }

      case 'update_interaction_preference': {
        const key = params.key as string
        const value = params.value
        if (!key) {
          setStatusBanner({ type: 'error', message: 'No preference key provided' })
          return
        }
        await apiFetch('/ivy/preferences', {
          method: 'PATCH',
          body: JSON.stringify({ key, value }),
        })
        setStatusBanner({ type: 'success', message: `Preference "${key}" noted` })
        break
      }

      default:
        setStatusBanner({ type: 'info', message: `Action "${action}" is not yet available` })
    }

    await audit(action, 1, params, sessionId, 'completed')
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Action failed'
    setStatusBanner({ type: 'error', message: `${action}: ${msg}` })
    await audit(action, 1, params, sessionId, 'failed', msg)
  }
}

// ── Tier 2 dispatcher ─────────────────────────────────────────────────────────

async function executeTier2(
  action: string,
  params: Record<string, unknown>,
  ctx: ExecutorContext
): Promise<void> {
  const { sessionId, podcastId, setStatusBanner, navigate } = ctx

  try {
    switch (action) {
      case 'schedule_episode': {
        const id = params.episodeId as string
        const datetime = params.datetime as string
        await updatePlannedEpisode(id, { target_publish_date: datetime })
        setStatusBanner({ type: 'success', message: 'Episode publish date set' })
        break
      }

      case 'add_deal': {
        await createDeal(params as Record<string, unknown>)
        setStatusBanner({ type: 'success', message: 'Deal added to pipeline' })
        break
      }

      case 'update_deal_status': {
        const dealId = params.dealId as string
        const status = params.status as string
        await updateDeal(dealId, { status })
        setStatusBanner({ type: 'success', message: `Deal status updated to ${status}` })
        break
      }

      case 'approve_roadmap_entry': {
        const roadmapId = params.roadmapId as string
        await updateRoadmapEntry(roadmapId, { status: 'accepted' })
        setStatusBanner({ type: 'success', message: 'Roadmap entry approved' })
        break
      }

      case 'trigger_guest_reenrichment': {
        const guestId = params.guestId as string
        await scoreGuest(guestId, podcastId)
        setStatusBanner({ type: 'success', message: 'Guest re-enrichment started' })
        break
      }

      case 'trigger_castback_index_run': {
        await apiFetch('/castback/index/trigger', { method: 'POST' })
        setStatusBanner({ type: 'success', message: 'CastBack index run started' })
        break
      }

      case 'update_castback_settings': {
        await apiFetch('/castback/settings', {
          method: 'PUT',
          body: JSON.stringify(params.payload ?? params),
        })
        setStatusBanner({ type: 'success', message: 'CastBack settings updated' })
        break
      }

      case 'schedule_launch_monitor': {
        const episodeId = params.episodeId as string
        await startLaunch(episodeId)
        setStatusBanner({ type: 'success', message: 'Launch monitor scheduled' })
        break
      }

      case 'submit_engine_feedback': {
        await apiFetch('/feedback/engine', {
          method: 'POST',
          body: JSON.stringify({ engine: params.engine, payload: params.payload }),
        })
        setStatusBanner({ type: 'success', message: 'Feedback submitted' })
        break
      }

      case 'update_user_preferences': {
        await updatePreferences(params as Parameters<typeof updatePreferences>[0])
        setStatusBanner({ type: 'success', message: 'Preferences updated' })
        break
      }

      case 'update_niche_classification': {
        await updatePreferences(params as Parameters<typeof updatePreferences>[0])
        setStatusBanner({ type: 'success', message: 'Niche classification updated' })
        break
      }

      case 'update_voice_profile_feedback': {
        await apiFetch('/voice/feedback', {
          method: 'POST',
          body: JSON.stringify(params),
        })
        setStatusBanner({ type: 'success', message: 'Voice profile feedback submitted' })
        break
      }

      case 'create_planned_episode': {
        const roadmapEntryId = params.roadmapId as string | undefined
        await createPlannedEpisode({
          podcast_id: podcastId,
          roadmap_entry_id: roadmapEntryId,
        } as Parameters<typeof createPlannedEpisode>[0])
        navigate('/brain')
        setStatusBanner({ type: 'success', message: 'New planned episode created' })
        break
      }

      case 'delete_planned_episode': {
        const id = params.plannedEpisodeId as string
        await deletePlannedEpisode(id)
        navigate('/brain')
        setStatusBanner({ type: 'success', message: 'Planned episode deleted' })
        break
      }

      case 'export_clip_to_descript': {
        // sendClipToDescript requires a clip_proposal_id and source_media_url which
        // are only available in the Episode Brain workspace UI. Navigate there so the
        // user can initiate the export with the full payload context.
        navigate('/brain')
        setStatusBanner({ type: 'info', message: 'Navigate to Episode Brain to export the clip to Descript' })
        break
      }

      case 'action_castback_alert': {
        const alertId = params.alertId as string
        const mode = params.mode as string
        await apiFetch(`/castback/alerts/${alertId}/action`, {
          method: 'POST',
          body: JSON.stringify({ mode }),
        })
        setStatusBanner({ type: 'success', message: 'CastBack alert action taken' })
        break
      }

      case 'trigger_platform_sync': {
        const platformId = params.platformId as string
        await syncPlatform(platformId)
        setStatusBanner({ type: 'success', message: 'Platform sync started' })
        break
      }

      case 'bulk_voice_analyze': {
        await bulkAnalyzeTranscribed(podcastId)
        setStatusBanner({ type: 'success', message: 'Bulk voice analysis started' })
        break
      }

      case 'add_competitor': {
        await addCompetitorShow({
          podcast_id: podcastId,
          competitor_name: params.showName as string,
          competitor_rss_url: params.rssUrl as string | undefined,
        } as Parameters<typeof addCompetitorShow>[0])
        setStatusBanner({ type: 'success', message: 'Competitor added to radar' })
        break
      }

      case 'remove_competitor': {
        const competitorId = params.competitorId as string
        await removeCompetitorShow(competitorId)
        setStatusBanner({ type: 'success', message: 'Competitor removed from radar' })
        break
      }

      case 'start_launch_monitor': {
        const episodeId = params.episodeId as string
        await startLaunch(episodeId)
        navigate('/launch')
        setStatusBanner({ type: 'success', message: 'Launch monitoring started' })
        break
      }

      case 'end_launch_monitor': {
        const launchEventId = params.launchEventId as string
        await endLaunch(launchEventId)
        setStatusBanner({ type: 'success', message: 'Launch monitoring ended' })
        break
      }

      case 'queue_clip_routing': {
        const clipId = params.clipId as string
        const targetPlatforms = (params.platforms ?? []) as import('../types/database').SocialPlatform[]
        await routeClipToSocial({ clipId, targetPlatforms })
        setStatusBanner({ type: 'success', message: 'Clip queued for social distribution' })
        break
      }

      case 'create_guest': {
        await createGuest({ name: params.name as string, ...(params.payload as object ?? {}) } as Parameters<typeof createGuest>[0])
        navigate('/guests')
        setStatusBanner({ type: 'success', message: 'Guest created' })
        break
      }

      case 'update_podcast_metadata': {
        await apiFetch(`/podcasts/${podcastId}`, {
          method: 'PATCH',
          body: JSON.stringify(params.payload ?? params),
        })
        setStatusBanner({ type: 'success', message: 'Podcast metadata updated' })
        break
      }

      default:
        setStatusBanner({ type: 'info', message: `Action "${action}" is not yet available` })
    }

    await audit(action, 2, params, sessionId, 'completed')
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Action failed'
    setStatusBanner({ type: 'error', message: `${action}: ${msg}` })
    await audit(action, 2, params, sessionId, 'failed', msg)
  }
}
